import { EventInput } from '@fullcalendar/core/index.js';
import { blue, green, red } from '@mui/material/colors';
import { DateTime, Interval } from 'luxon';
import {
  DayOfWeek,
  OrderType,
} from 'api/ordering-service/ordering-service.types';
import { Option } from 'util/multi-select';
import i18n from 'app-i18n';
import { daysOfWeekOptions } from 'components/forms/fields/days-multiple-select-field/days-multiple-select-field.utils';

export type SetHoursDrawerDataProps = {
  id?: string;
  title: string;
  start: DateTime;
  end: DateTime;
  allDayEvents?: EventInput[];
};

export type SetHoursFormDataProps = {
  eventId?: string;
  daysOfWeek?: Option[];
  dateRange?: string[];
  timeRange?: string[];
  orderType?: OrderType[];
  pickupClosed?: boolean;
  deliveryClosed?: boolean;
};

export type LocationHoursOrderType =
  | OrderType
  | 'PICKUP_CLOSED'
  | 'DELIVERY_CLOSED';

export const dayOfWeekMap: Record<DayOfWeek, string> = {
  SUNDAY: '0',
  MONDAY: '1',
  TUESDAY: '2',
  WEDNESDAY: '3',
  THURSDAY: '4',
  FRIDAY: '5',
  SATURDAY: '6',
};

export const DUMMY_EVENT_ID = 'DUMMY_EVENT_ID';

export const createDummyEvent = (
  start: DateTime,
  end: DateTime,
  title: string,
  orderType: LocationHoursOrderType,
  allDay: boolean = false,
): EventInput => {
  return {
    id: DUMMY_EVENT_ID,
    title,
    start: start.toJSDate(),
    end: end.toJSDate(),
    extendedProps: {
      orderType,
      eventType: 'LocationHours',
    },
    color: allDay ? red[500] : orderType === 'PICKUP' ? blue[500] : green[500],
    allDay,
  };
};

export const convertToSetHoursFormData = ({
  id,
  title,
  start,
  end,
  allDayEvents,
}: SetHoursDrawerDataProps): SetHoursFormDataProps => {
  let orderType: OrderType[] = [];

  const dayOfWeek = start?.setLocale('en-US').weekdayShort.toUpperCase();

  const daysOfWeek = [
    {
      id: dayOfWeek,
      label: daysOfWeekOptions().find(option => option.id === dayOfWeek)?.label,
    },
  ];

  const dateRange = [start.startOf('day').toISO(), end.endOf('day').toISO()];

  const timeRange = [start.toISO(), end.toISO()];

  const pickupClosed = allDayEvents?.some(
    event => event.extendedProps?.orderType === 'PICKUP_CLOSED',
  );

  const deliveryClosed = allDayEvents?.some(
    event => event.extendedProps?.orderType === 'DELIVERY_CLOSED',
  );

  if (pickupClosed && deliveryClosed) {
    orderType = [];
  } else {
    switch (title) {
      case i18n.t('LocationHours.eventTitles.pickup'):
        orderType = ['PICKUP'];
        break;
      case i18n.t('LocationHours.eventTitles.delivery'):
        orderType = ['DELIVERY'];
        break;
      default:
        orderType = [];
    }
  }

  return {
    eventId: id ?? null,
    orderType,
    daysOfWeek,
    dateRange,
    timeRange,
    pickupClosed,
    deliveryClosed,
  };
};

// Helper function to filter all-day events on a given date
export const filterAllDayEventsSelectedRange = (
  events: EventInput[],
  start,
  end,
): EventInput[] => {
  const newEventInterval = Interval.fromDateTimes(start, end);

  return events
    .filter(event => event.allDay)
    .filter(event => {
      return newEventInterval.contains(DateTime.fromISO(String(event.start)));
    });
};

// Function to disable past dates
export const isPastDate = date => {
  return (
    DateTime.fromJSDate(date).startOf('day') < DateTime.local().startOf('day')
  );
};
